import { NavLink } from 'react-router-dom';
import { Tooltip } from '@fluentui/react-components';
import { PanelLeftContract20Regular, PanelLeftExpand20Regular } from '@fluentui/react-icons';
import {
  ADMIN_NAV,
  PRIMARY_NAV,
  TEAMS_TAB_ROUTES,
  type NavItem,
} from '@/components/layout/nav-items';
import { cn } from '@/lib/cn';

/**
 * Desktop side navigation.
 *
 * Dense but breathable: 40px rows, a 3px brand rail on the active item and an
 * optional collapsed mode (icons only) for narrow Teams windows.
 */

export interface SideNavProps {
  collapsed: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  /** Drops the entries Teams already draws as tabs above the content. */
  hideTeamsTabs?: boolean;
  className?: string;
}

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }): JSX.Element {
  const link = (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 text-body font-medium transition-colors duration-fast ease-standard',
          'h-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-brand-50 text-ink-brand dark:bg-brand-900/60'
            : 'text-ink-secondary hover:bg-subtle hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-pill bg-gold-500 transition-opacity duration-fast dark:bg-gold-400',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <span className="grid h-5 w-5 shrink-0 place-items-center">
            {isActive ? item.activeIcon : item.icon}
          </span>
          {collapsed ? null : <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip content={item.label} relationship="label" positioning="after" withArrow>
      {link}
    </Tooltip>
  );
}

export function SideNav({
  collapsed,
  onToggle,
  isAdmin,
  hideTeamsTabs = false,
  className,
}: SideNavProps): JSX.Element {
  const primary = hideTeamsTabs
    ? PRIMARY_NAV.filter((item) => !TEAMS_TAB_ROUTES.has(item.to))
    : PRIMARY_NAV;

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        'z-sidenav hidden shrink-0 flex-col gap-1 border-r border-edge bg-card px-3 py-3 transition-[width] duration-base ease-standard md:flex',
        collapsed ? 'w-sidenav-collapsed' : 'w-sidenav',
        className,
      )}
    >
      <ul className="flex flex-col gap-0.5">
        {primary.map((item) => (
          <li key={item.to}>
            <NavRow item={item} collapsed={collapsed} />
          </li>
        ))}
      </ul>

      {isAdmin ? (
        <>
          {/* The amber marks where the administrative half of the rail starts.
              It is ornament: the heading already says so in words, and the
              section is equally clear without colour. */}
          <div className={cn('mt-5 mb-1', collapsed ? 'px-0' : 'px-3')}>
            {collapsed ? (
              <span aria-hidden="true" className="mx-auto block h-px w-6 bg-gold-500/60" />
            ) : (
              <span className="flex items-center gap-2">
                <span aria-hidden="true" className="h-px w-3 shrink-0 rounded-pill bg-gold-500" />
                <span className="section-label">Administración</span>
              </span>
            )}
          </div>
          <ul className="flex flex-col gap-0.5">
            {ADMIN_NAV.map((item) => (
              <li key={item.to}>
                <NavRow item={item} collapsed={collapsed} />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* Amber hairline closing the rail, matching the one under the header. */}
      <div className="mt-auto border-t border-gold-500/25 pt-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expandir navegación' : 'Contraer navegación'}
          className={cn(
            'flex h-10 w-full items-center gap-3 rounded-lg px-3 text-body font-medium text-ink-muted',
            'transition-colors duration-fast ease-standard hover:bg-subtle hover:text-ink',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            collapsed && 'justify-center px-0',
          )}
        >
          <span className="grid h-5 w-5 shrink-0 place-items-center">
            {collapsed ? <PanelLeftExpand20Regular /> : <PanelLeftContract20Regular />}
          </span>
          {collapsed ? null : <span className="truncate">Contraer</span>}
        </button>
      </div>
    </nav>
  );
}
